import axios from 'axios';
import { Localization } from '../localization/localization';
import { RegistrationError } from './RegistrationErorr';
import { RegistrationResult } from './emailRegistration';
import { configureHttpAuth } from '../startup/configureHttpAuth';
import { getHubApi } from '../general/modules/accountServiceApi/getAccountServiceApi';
import { getErrorMessage, isExpectedRegistrationError } from '../general/modules/registration_popup/getErrorMessage';
import { getUrlToLoginViaToken } from '../login/getUrlToLoginViaToken';
import { isLoginAccepted } from '../login/loginSuccessMarker';
import { addLoginQueryParams } from '../utils/url';
import { GtmSender } from '../marketing/GtmSender';
import { SystemEvents } from '../constants/gameEvents';
import { Environment } from '../environment/environment';
import { container } from '../utils/container/container';
import { FeatureFlagsLoader } from '../utils/feature-flags/feature-flags-loader';
import { FlagNames } from '../utils/feature-flags/flag-names';
import { ServiceIds } from '../utils/container/service-ids';

/**
 * OTP registration for the popup wizards that build their own form.
 * The two-step popups use the handlers in registration_popup/registrationWithOtp instead.
 */
export const OTP_LENGTH = 6;

// The code is spread over one input per digit.
export function readOtpValue($root: JQuery): string {
    return $('.pin-input', $root).toArray()
        .map((input: HTMLInputElement) => input.value)
        .join('')
        .toUpperCase();
}

export function isOtpRegistrationOn(): boolean {
    return container.get<FeatureFlagsLoader>(ServiceIds.FEATURE_FLAG_LOADER)
        .get(FlagNames.SITE_OTP_REGISTER)
        .isOn();
}

export async function requestOtpCode(email: string): Promise<RegistrationResult> {
    try {
        const hubApi = getHubApi();
        await hubApi.requestEmailOtpReg(window.app.deviceIdentifier, {
            current_entry_point: window.app.config.current_entry_point,
            email,
            locale: window.app.config.locale
        });

        return [true, void (0)];
    } catch (exception) {
        return [false, new RegistrationError(toMessage(exception, 'otp code request'))];
    }
}

/* An account whose login call failed. The code is spent by then, so a retry has to reuse the
   tether; registering again would be refused. The holder belongs to one popup, because a page
   can carry several and a tether must not cross between them. */
export class PendingLogin {
    private email: string = '';
    private url: string = '';

    public get(email: string): string|null {
        return this.url && this.email === email ? this.url : null;
    }

    public keep(email: string, url: string): void {
        this.email = email;
        this.url = url;
    }

    public clear(): void {
        this.email = '';
        this.url = '';
    }
}

export async function registerWithOtpCode(
    email: string,
    otp: string,
    pendingLogin: PendingLogin
): Promise<RegistrationResult> {
    let loginUrl = pendingLogin.get(email);

    if (!loginUrl) {
        try {
            const hubApi = getHubApi();
            const response = await hubApi.emailOtpRegistration(window.app.deviceIdentifier, {
                current_entry_point: window.app.config.current_entry_point,
                email,
                locale: window.app.config.locale,
                otp
            });

            window.app.user_huid = response.accountId;
            loginUrl = addLoginQueryParams(getUrlToLoginViaToken(response.tether.token, response.tether.suid));
            pendingLogin.keep(email, loginUrl);
        } catch (exception) {
            return [false, new RegistrationError(toMessage(exception, 'otp registration'))];
        }
    }

    try {
        const response = await axios.get(loginUrl);

        // Only the success script means the session exists.
        if (!isLoginAccepted(response?.data)) {
            console.error('login by token refused');
            window.Sentry.captureMessage('login by token refused');

            return [false, new RegistrationError(Localization._n('errors.common_error'))];
        }
    } catch (exception) {
        // Report the status only. The url carries the tether token, so neither the url nor
        // the exception may reach Sentry. The account exists, so the tether stays for a retry.
        const status = exception?.response?.status ?? 'no response';
        console.error(`login by token failed: ${status}`);
        window.Sentry.captureMessage(`login by token failed: ${status}`);

        return [false, new RegistrationError(Localization._n('errors.common_error'))];
    }

    pendingLogin.clear();
    configureHttpAuth();

    if (Environment.isLanding()) {
        GtmSender.fireClient(SystemEvents.EMAIL_REG_SUCCESS);
        GtmSender.fireClient(SystemEvents.IS_LANDING_REG);
    }

    return [true, void (0)];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toMessage(exception: any, operation: string): string {
    if (exception?.isAxiosError) {
        // The hub wrapper rethrows a transport failure as it came. Report the status and
        // the operation only: the request carries the address and the code.
        const status = exception?.response?.status ?? 'no response';
        console.error(`${operation} failed: ${status}`);
        window.Sentry.captureMessage(`${operation} failed: ${status}`);

        return Localization._n('errors.common_error');
    }

    if (!isExpectedRegistrationError(exception?.code)) {
        console.error(exception);
        window.Sentry.captureException(exception);
    }

    if (exception?.name === 'RpcReceivedError') {
        return getErrorMessage(exception.code, exception.data);
    }

    return Localization._n('errors.common_error');
}
